/**
 *
 * Assessment/index
 *
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import {
  MuiThemeProvider,
  createTheme,
  makeStyles,
} from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import lvTheme from 'utils/theme';
import LoadingOverlay from 'components/LoadingOverlay';
import {
  makeSelectAuth,
  makeSelectProfile,
  makeSelectProcessing as makeSelectAuthProcessing,
} from 'containers/Auth/selectors';
import {
  makeSelectAICCUser,
  makeSelectClientDetails,
  makeSelectLanguage,
} from 'containers/Main/selectors';
import { makeSelectProcessing as makeSelectSaveProcessing } from 'containers/Assessments/selectors';
import { makeSelectProcessing as makeSelectTopicProcessing } from 'containers/Topics/selectors';
import { getColorsConfig } from 'containers/Assessments/utils';
import { getSavedAssessments } from 'containers/Assessments/actions';
import MixPanel from 'utils/mixpanelService';
import PageMeta from 'components/PageMeta';
import { isEmbedded, windowScrollTo } from 'utils/embedded';
import { makeSelectProcessing, makeSelectAnswersProcessing } from './selectors';
import Header from './Header';
import AssessmentSurvey from './AssessmentSurvey';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
  },
  container: {
    [theme.breakpoints.up('1140')]: {
      width: 1140,
      padding: '24px 14px',
    },
    width: '100%',
    padding: '24px 0',
  },
}));

const stateSelector = createStructuredSelector({
  aiccUser: makeSelectAICCUser(),
  answersProcessing: makeSelectAnswersProcessing(),
  auth: makeSelectAuth(),
  authProcessing: makeSelectAuthProcessing(),
  clientDetails: makeSelectClientDetails(),
  language: makeSelectLanguage(),
  processing: makeSelectProcessing(),
  profile: makeSelectProfile(),
  saveProcessing: makeSelectSaveProcessing(),
  topicProcessing: makeSelectTopicProcessing(),
});

const Assessment = ({
  assessment,
  assessmentPath,
  trackingProps,
  spanFullWidth,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const {
    aiccUser,
    answersProcessing,
    auth,
    authProcessing,
    clientDetails,
    processing,
    profile,
    saveProcessing,
    topicProcessing,
  } = useSelector(stateSelector);

  const { slug } = match.params;
  const isEmbed = isEmbedded();

  useEffect(() => {
    const url = `${assessmentPath}/page1${location.search || ''}`;
    if (isEmbed) {
      history.push(url);
    } else {
      history.replace(url);
    }
  }, [history, location.search, assessmentPath, isEmbed]);

  useEffect(() => {
    if (auth.isLoaded && !auth.isEmpty && _get(assessment, 'items.0')) {
      dispatch(getSavedAssessments());

      const topicsList = (_get(assessment, 'items.0.fields.topics') || [])
        .map(topic => _get(topic, 'fields.title', null))
        .filter(topic => topic !== null)
        .map(topicName => `[${topicName}]`)
        .join();

      MixPanel.track(`Assessment viewed`, {
        name: _get(assessment, 'items.0.fields.name'),
        topic_name: topicsList,
        entryId: assessment.items[0].sys.id,
        ...trackingProps,
      });
    }
  }, [_get(assessment, 'items.0'), auth, dispatch]);

  const handleStartButtonClick = () => {
    const surveyWrapper = document.getElementById('survey-wrapper');
    const header = document.getElementById('app-header');
    const { top } = surveyWrapper.getBoundingClientRect();

    windowScrollTo(window.scrollY + (top - header.offsetHeight));

    MixPanel.track('Assessment Page - Start Clicked', {
      name: _get(assessment, 'items.0.fields.name'),
      slug: _get(assessment, 'items.0.fields.slug'),
      entryId: _get(assessment, 'items.0.sys.id'),
    });
  };

  const isAICC = _get(clientDetails, 'aicc') || false;

  const { color } = getColorsConfig(assessment.items[0]);

  const defaultTheme = lvTheme();
  const customTheme = {
    ...defaultTheme,
    palette: {
      primary: {
        ...defaultTheme.palette.primary,
        bgColor: color.main,
        font: color.font,
      },
      secondary: {
        ...defaultTheme.palette.secondary,
      },
      text: {
        ...defaultTheme.palette.text,
        secondary: 'rgba(0, 0, 0, 0.54)',
      },
    },
  };
  const theme = createTheme(customTheme);
  return (
    <>
      <PageMeta
        data={assessment}
        type="assessment"
        canonicalUrl={`${
          window.location.origin
        }/assessments/${slug}${location.search || ''}`}
      />
      <MuiThemeProvider theme={theme}>
        <Header
          isAdmin={
            (profile.role === 'admin' || profile.role === 'contentAdmin') &&
            profile.showContentfulTools === true &&
            !isEmbed
          }
          onStartButtonClick={handleStartButtonClick}
          assessment={assessment}
        />
      </MuiThemeProvider>
      <Box className={classes.root}>
        <Box className={classes.container}>
          <LoadingOverlay
            show={
              processing ||
              topicProcessing ||
              authProcessing ||
              saveProcessing ||
              answersProcessing ||
              (isAICC && _isEmpty(aiccUser))
            }
          />
          <AssessmentSurvey
            theme={theme}
            assessment={assessment}
            assessmentPath={assessmentPath}
            trackingProps={trackingProps}
            spanFullWidth={spanFullWidth}
          />
        </Box>
      </Box>
    </>
  );
};

export default Assessment;
